<ra-modal
    [title]="titleKey() | translate"
    [size]="'large'"
    (close)="close.emit()">

    <div class="document-batch">

        <!-- Passos: o 2 não volta para o 1 — o pacote já está rodando. -->
        <div class="steps">
            <span class="step" [class.done]="step() === 'progress'" [class.active]="step() === 'select'">
                @if (step() === 'progress') {
                    <ra-icon icon="check" [size]="16" />
                } @else {
                    <b>1</b>
                }
                {{ 'order.documentBatch.step1' | translate }}
            </span>
            <i class="bar" [class.on]="step() === 'progress'"></i>
            <span class="step" [class.active]="step() === 'progress'" [class.done]="batchService.ready()">
                @if (batchService.ready()) {
                    <ra-icon icon="check" [size]="16" />
                } @else {
                    <b>2</b>
                }
                {{ 'order.documentBatch.step2' | translate }}
            </span>
        </div>

        <!-- ============ PASSO 1: ESCOLHER ============ -->
        @if (step() === 'select') {

            @if (showFilter()) {
            <div class="filter-row">
                <label class="search">
                    <ra-icon icon="search-normal" [size]="15" />
                    <input
                        type="text"
                        [value]="query()"
                        [placeholder]="'order.documentBatch.searchPlaceholder' | translate"
                        (input)="query.set($any($event.target).value)" />
                </label>
            </div>
            }

            @if (documents().length > 1) {
            <label class="select-all">
                <input type="checkbox" [checked]="allSelected()" (change)="toggleAll()" />
                <span class="grow">{{ 'order.documentBatch.selectAll' | translate }}</span>
                <span class="muted">
                    {{ 'order.documentBatch.selectedOf' | translate: { selected: selectedIds().length, total: documents().length } }}
                </span>
            </label>
            }

            <!-- Dois grupos: o que o pacote vai gerar e o que já tem PDF e entra de graça.
                 Numa lista de 42 isso era invisível, e o botão prometia 42 gerações. -->
            <div class="doc-list">
                @if (pendingDocuments().length > 0) {
                    <div class="group-head">
                        <b>{{ 'order.documentBatch.groupToGenerate' | translate }}</b>
                        @if (pendingDocuments().length > 1) {
                            <span class="muted">
                                {{ groupCountKey(pendingDocuments().length) | translate: { count: pendingDocuments().length } }}
                                · {{ groupSizeGB(pendingDocuments()) | formatFileSize: 'GB' }}
                            </span>
                        }
                    </div>
                }
                @for (document of pendingDocuments(); track document._id) {
                    <label class="doc-item" [class.off]="!isChecked(document._id)">
                        <input
                            type="checkbox"
                            [checked]="isChecked(document._id)"
                            (change)="toggle(document._id)" />
                        <span class="doc-info">
                            <span class="doc-title">{{ document.title }}</span>
                            <span class="doc-sub">{{ document.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                        </span>
                        <span class="muted">{{ sizeOf(document) | formatFileSize: 'GB' }}</span>
                    </label>
                }

                @if (readyDocuments().length > 0 && showReady()) {
                    <div class="group-head ready">
                        <b>{{ 'order.documentBatch.groupReady' | translate }}</b>
                    </div>
                }
                @for (document of readyExpanded() ? readyDocuments() : []; track document._id) {
                    <label class="doc-item" [class.off]="!isChecked(document._id)">
                        <input
                            type="checkbox"
                            [checked]="isChecked(document._id)"
                            (change)="toggle(document._id)" />
                        <span class="doc-info">
                            <span class="doc-title">{{ document.title }}</span>
                            <span class="doc-sub">{{ document.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                        </span>
                        <span class="muted">{{ sizeOf(document) | formatFileSize: 'GB' }}</span>
                    </label>
                }

                @if (visibleDocuments().length === 0) {
                    <p class="empty">{{ 'order.documentBatch.emptyFilter' | translate }}</p>
                }
            </div>

            <!-- Os que já têm PDF não precisam ocupar a lista: são a maioria e não há
                 decisão a tomar sobre eles. Fica o resumo, e quem quiser desmarcar um
                 abre a lista aqui. -->
            @if (readyDocuments().length > 0) {
                <div class="ready-note">
                    <ra-icon icon="check" [size]="15" varColor="accentGreen" />
                    <span class="grow">
                        {{ readySummaryKey() | translate: { count: readyDocuments().length } }}
                    </span>
                    <button type="button" class="link" (click)="toggleReady()">
                        {{ (readyExpanded()
                            ? 'order.documentBatch.readyHide'
                            : 'order.documentBatch.readyShow') | translate }}
                    </button>
                </div>
            }

            <div class="footer">
                <span class="footer-info">
                    <b>{{ selectedSummaryKey() | translate: { count: selectedIds().length } }}</b>
                    <!-- O aviso de crédito mora aqui em vez de num bloco só dele: é uma
                         frase, e o rodapé já é o lugar onde se olha antes de confirmar. -->
                    <span class="muted">
                        {{ selectedSizeGB() | formatFileSize: 'GB' }}
                        · {{ chargeKey() | translate: { count: toGenerateCount() } }}
                    </span>
                </span>
                <div class="footer-actions">
                    <ra-button
                        [label]="'order.common.cancel' | translate"
                        [type]="'primary-outline'"
                        (click)="close.emit()" />
                    <ra-button
                        [label]="generateLabelKey() | translate: { count: toGenerateCount() || selectedIds().length }"
                        [type]="'primary'"
                        [loading]="batchService.starting()"
                        [disabled]="selectedIds().length === 0"
                        (click)="confirm()" />
                </div>
            </div>
        }

        <!-- ============ PASSO 2: GERAR E BAIXAR ============ -->
        @else {

            @if (batchService.ready()) {
                <div class="zip-card">
                    <div class="zip-icon"><ra-icon icon="document-download" [size]="22" /></div>
                    <div class="zip-info">
                        <b>{{ 'order.documentBatch.zipReadyTitle' | translate: { count: batchService.succeeded() } }}</b>
                        <span>{{ 'order.documentBatch.zipExpires' | translate }}</span>
                    </div>
                    <ra-button
                        [label]="(batchService.downloading()
                            ? 'order.documentBatch.downloading'
                            : 'order.documentBatch.downloadZip') | translate"
                        [type]="'primary'"
                        [loading]="batchService.downloading()"
                        (click)="download()" />
                </div>
            } @else {
                @if (batchService.zipping()) {
                    <!-- Contador e barra cheia diziam "acabou" bem no meio da etapa mais
                         demorada. Aqui o bloco passa a falar do PACOTE, e a barra é
                         indeterminada: ninguém sabe quanto falta para o ZIP. -->
                    <div class="zip-head">
                        <i class="spinner spinner--sm"></i>
                        <span class="zip-head__text">
                            <b>{{ 'order.documentBatch.zippingTitle' | translate }}</b>
                            <span>
                                @if (batchService.zipTotal() > 0) {
                                    <!-- O servidor conta cada arquivo que entra: é o que
                                         separa "está demorando" de "travou". -->
                                    {{ 'order.documentBatch.zippingFiles' | translate: {
                                        done: batchService.zipDone(),
                                        total: batchService.zipTotal()
                                    } }}
                                    @if (batchService.zipTotalBytes() > 0) {
                                        · <b>{{ batchService.zipPercent() }}%</b>
                                    }
                                } @else {
                                    {{ 'order.documentBatch.zippingHint' | translate: { count: batchService.succeeded() } }}
                                }
                            </span>
                        </span>
                    </div>
                    @if (batchService.zipTotal() > 0) {
                        <div class="progress-bar"><i [style.width.%]="batchService.zipPercent()"></i></div>
                    } @else {
                        <div class="progress-bar indeterminate"><i></i></div>
                    }
                } @else {
                    <div class="progress-head">
                        <span class="count">
                            <b>{{ batchService.done() }}</b>
                            {{ 'order.documentBatch.ofTotalReady' | translate: { total: batchService.total() } }}
                        </span>
                    </div>
                    <div class="progress-bar"><i [style.width.%]="batchService.percent()"></i></div>
                }
            }

            <!-- Ordenada por atenção: o que falta fica em cima, o pronto desce. -->
            <div class="doc-list">
                @for (item of orderedItems(); track item.documentId) {
                    <div
                        class="doc-item"
                        [class.ok]="item.status === itemStatus.COMPLETED"
                        [class.bad]="item.status === itemStatus.ERROR">
                        @if (item.status === itemStatus.COMPLETED) {
                            <ra-icon icon="check" [size]="18" varColor="accentGreen" />
                        } @else if (item.status === itemStatus.ERROR) {
                            <ra-icon icon="close-circle" [size]="18" varColor="accentRed" />
                        } @else {
                            <i class="spinner"></i>
                        }
                        <span class="doc-info">
                            <span class="doc-title">{{ item.title }}</span>
                            @if (item.status === itemStatus.ERROR) {
                                <span class="doc-sub bad">{{ itemErrorKey(item) | translate }}</span>
                            } @else if (item.status === itemStatus.PENDING) {
                                @if (batchService.isRendering(item.documentId)) {
                                    <!-- Em render: o engine publica marcos no canal do
                                         documento, então dá para mostrar o quanto falta.
                                         Antes do primeiro marco, "começando a geração". -->
                                    @let percent = batchService.itemPercent(item.documentId);
                                    <span class="doc-sub rendering">
                                        @if (percent > 0) {
                                            {{ 'order.documentBatch.itemRendering' | translate }}
                                            <b>{{ percent }}%</b>
                                        } @else {
                                            {{ 'order.documentBatch.itemStarting' | translate }}
                                        }
                                    </span>
                                    <span class="item-bar">
                                        <i [style.width.%]="percent"></i>
                                    </span>
                                } @else {
                                    <span class="doc-sub">{{ 'order.documentBatch.itemPending' | translate }}</span>
                                }
                            }
                        </span>

                        <!-- Ação da linha: um documento que não vem não pode obrigar a
                             cancelar o pacote inteiro. -->
                        @if (item.status === itemStatus.PENDING && batchService.running()) {
                            <button
                                type="button"
                                class="item-action"
                                [disabled]="batchService.isItemBusy(item.documentId)"
                                [attr.aria-label]="'order.documentBatch.skipItem' | translate"
                                [raTooltip]="{ text: ('order.documentBatch.skipItemHint' | translate), position: 'right' }"
                                (click)="skipItem(item.documentId)">
                                <ra-icon icon="close" [size]="16" />
                            </button>
                        } @else if (item.status === itemStatus.ERROR && canRetry()) {
                            <button
                                type="button"
                                class="item-action"
                                [disabled]="batchService.isItemBusy(item.documentId)"
                                [attr.aria-label]="'order.documentBatch.retryItem' | translate"
                                [raTooltip]="{ text: ('order.documentBatch.retryItemHint' | translate), position: 'right' }"
                                (click)="retryItem(item.documentId)">
                                <ra-icon icon="rotate-right" [size]="16" />
                            </button>
                        }
                    </div>
                }
            </div>

            <div class="footer">
                <span class="footer-info">
                    @if (failedItems().length > 0) {
                        <span class="danger">
                            {{ 'order.documentBatch.failedCount' | translate: { count: failedItems().length } }}
                        </span>
                    } @else if (!batchService.ready()) {
                        <span class="muted">{{ 'order.documentBatch.notifyNote' | translate }}</span>
                    }
                </span>
                <div class="footer-actions">
                    @if (batchService.ready()) {
                        <ra-button
                            [label]="'order.common.close' | translate"
                            [type]="'primary-outline'"
                            (click)="close.emit()" />
                    } @else {
                        <ra-button
                            [label]="'order.documentBatch.cancelAll' | translate"
                            [type]="'primary-outline'"
                            (click)="cancel()" />
                        <ra-button
                            [label]="'order.documentBatch.minimize' | translate"
                            [type]="'primary'"
                            (click)="minimize()" />
                    }
                </div>
            </div>
        }
    </div>
</ra-modal>
